import { useEffect, useRef, useState, useCallback } from "react";

interface Props {
  storageKey: string;
  watch: any;
  reset: any;
  step: number;
  setStep: (step: number) => void;
  enabled?: boolean;
}

export default function useCompanyDraft({
  storageKey,
  watch,
  reset,
  step,
  setStep,
  enabled = true,
}: Props) {
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">(
    "idle"
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const isInitialLoad = useRef<boolean>(true);
  const lastSavedValues = useRef<any>(null);
  const hasUserInteracted = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    const subscription = watch((values: any) => {
      if (isInitialLoad.current) return;

      if (!hasUserInteracted.current) {
        const isEmpty = Object.keys(values).every((key) => {
          const val = values[key];
          if (Array.isArray(val)) return val.length === 0;
          if (val === null || val === undefined || val === "" || val === false)
            return true;
          if (typeof val === "object") {
            return Object.keys(val).every((k) => {
              const v = val[k];
              return v === null || v === undefined || v === "" || v === false;
            });
          }
          return false;
        });

        if (isEmpty) return;

        hasUserInteracted.current = true;
      }

      if (JSON.stringify(lastSavedValues.current) === JSON.stringify(values))
        return;

      setSaveStatus("saving");

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        try {
          const draftData = {
            values,
            step,
            timestamp: Date.now(),
          };
          localStorage.setItem(storageKey, JSON.stringify(draftData));
          lastSavedValues.current = values;
          setSaveStatus("saved");

          setTimeout(() => {
            setSaveStatus((prev) => (prev === "saved" ? "idle" : prev));
          }, 2000);
        } catch (error) {
          console.error("Error saving draft:", error);
          setSaveStatus("idle");
        }
      }, 1000);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [watch, step, storageKey, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const savedDraft = localStorage.getItem(storageKey);
    if (!savedDraft) {
      isInitialLoad.current = false;
      return;
    }

    try {
      const parsed = JSON.parse(savedDraft);

      // const isExpired =
      //   parsed.timestamp &&
      //   Date.now() - parsed.timestamp > 7 * 24 * 60 * 60 * 1000;

      // if (isExpired) {
      //   localStorage.removeItem(storageKey);
      //   isInitialLoad.current = false;
      //   return;
      // }

      if (parsed.values && Object.keys(parsed.values).length > 0) {
        const hasValues = Object.values(parsed.values).some((val) => {
          if (Array.isArray(val)) return val.length > 0;
          if (val === null || val === undefined || val === "" || val === false)
            return false;
          if (typeof val === "object") {
            return Object.values(val).some(
              (v) => v !== null && v !== undefined && v !== "" && v !== false
            );
          }
          return true;
        });

        if (hasValues) {
          reset(parsed.values);
          lastSavedValues.current = parsed.values;
          hasUserInteracted.current = true;
        }
      }
      if (parsed.step && typeof parsed.step === "number") {
        setStep(parsed.step);
      }
    } catch (error) {
      console.error("Error restoring draft:", error);
      localStorage.removeItem(storageKey);
    } finally {
      isInitialLoad.current = false;
    }
  }, [enabled, reset, setStep, storageKey]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    lastSavedValues.current = null;
    hasUserInteracted.current = false;
  }, [storageKey]);

  const resetInteraction = useCallback(() => {
    hasUserInteracted.current = false;
  }, []);

  return {
    saveStatus,
    clearDraft,
    resetInteraction,
  };
}
